import React, { useState } from 'react'
import { IContextMenuState, IUserObject } from 'types'
import Badge from 'components/badge'
import Button from 'components/button'
import Popover, { popoverPlacement } from 'components/popover'
import { InlineRow } from 'views/settings/components/settingsCard'

const POPOVER_WIDTH = 240

/**
 * "team manager", underlined, opening a popover that lists the team's managers, as a webhook's "3 events" opens
 * its events.
 */
const ManagersPopover: React.FC<{ team: string; managers: string[] }> = ({ team, managers }) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  return (
    <>
      <Button
        internalType="transparent"
        aria-haspopup="dialog"
        aria-expanded={!!at}
        className="h-auto p-0 align-baseline text-[13px] font-normal text-body underline underline-offset-2 decoration-body/40 hover:bg-transparent hover:text-body hover:decoration-body"
        // The popover closes on a press outside it, and this button counts as outside: while it's open, keep the
        // press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (at) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAt(
            at
              ? null
              : popoverPlacement(event.currentTarget.getBoundingClientRect(), {
                  align: 'start',
                  width: POPOVER_WIDTH,
                  height: managers.length * 24 + 60
                })
          )
        }
      >
        team manager
      </Button>
      <Popover
        id="team-managers"
        at={at}
        onClose={() => setAt(null)}
        label={`Managers of the ${team} team`}
        title="Team managers"
        width={POPOVER_WIDTH}
      >
        <ul className="flex max-h-[280px] flex-col gap-1 overflow-auto">
          {managers.map((name) => (
            <li key={name} className="truncate text-[13px] leading-5 text-body">
              {name}
            </li>
          ))}
        </ul>
      </Popover>
    </>
  )
}

/** The account's team, in name order: the server's order changes from one load to the next. */
const managersOf = (me: IUserObject) => [...(me.team?.managerNames ?? [])].sort((a, b) => a.localeCompare(b))

/**
 * The account's organisation, team and plan, as rows of the Account card. A paid seat in an organisation
 * comes from one of its teams: a pool of paid seats, assigned to people by the team's managers.
 */
const AccountRows: React.FC<{ me: IUserObject }> = ({ me }) => {
  const paid = me.subscriptionLevel !== 0
  const team = me.team

  return (
    <>
      {team && (
        <InlineRow label="Organisation">
          <span className="font-medium text-body">{team.orgName}</span>
        </InlineRow>
      )}
      {team && (
        <InlineRow label="Team">
          <span className="font-medium text-body">{team.teamName}</span>
        </InlineRow>
      )}
      <InlineRow
        label="Plan"
        description={
          paid
            ? 'A paid seat can create and configure hubs, documents and tables, including their columns, views, permissions and integrations.'
            : 'A free account can view data, comment and add rows, but cannot create or configure hubs, documents or tables.'
        }
      >
        {paid && me.isOnFreeTrial && (
          <span className="text-muted">
            Free trial: {me.freeTrialDaysRemaining} {me.freeTrialDaysRemaining === 1 ? 'day' : 'days'} left
          </span>
        )}
        <Badge tone={paid ? 'blue' : 'muted'} size="large">
          {paid ? 'Paid seat' : 'Free'}
        </Badge>
      </InlineRow>
    </>
  )
}

/**
 * The Account card's footer, for an account whose seat comes from an organisation's team: who manages the
 * account, and the team managers to contact, in a popover.
 */
export const AccountFooter: React.FC<{ me: IUserObject }> = ({ me }) => {
  const managers = managersOf(me)
  if (!me.team) return null
  return (
    <p className="flex-1 text-muted">
      This account is managed by {me.team.orgName}.{' '}
      {managers.length ? (
        <>
          For help with access, contact a <ManagersPopover team={me.team.teamName} managers={managers} />.
        </>
      ) : (
        'For help with access, contact a team manager.'
      )}
    </p>
  )
}

/**
 * The Account card's footer for a free account: an invitation to upgrade. A paid seat comes from an
 * organisation's admins, who the page can't name.
 */
export const UpgradeFooter: React.FC = () => (
  <div className="flex-1">
    <div className="font-medium text-body">Upgrade to create and edit on Morta</div>
    <p className="mt-0.5 text-muted">Contact an organisation admin to unlock a paid seat.</p>
  </div>
)

export default AccountRows
